import * as React from "react";
import Box from "@mui/material/Box";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import {
  GridRowModesModel,
  GridRowModes,
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridEventListener,
  GridRowId,
  GridRowModel,
  GridRowEditStopReasons,
} from "@mui/x-data-grid";
import { IBankAccount } from "src/interface/IBankAccount";
import { EditToolbarBankAccount } from "./EditToolbarBankAccount";
import { IAppUser } from "src/interface/IUser";

interface BankAccountProps {
  userData?: IAppUser;
  onClose: () => void;
  refetchUser: () => void;
}

export default function BankAccountForm({
  onClose,
  userData,
  refetchUser,
}: BankAccountProps) {
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>(
    {}
  );
  const [rows, setRows] = React.useState<IBankAccount[]>([]);

  const handleRowEditStop: GridEventListener<"rowEditStop"> = (
    params,
    event
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };
  const handleDeleteClick = (id: GridRowId) => () => {
    setRows(rows?.filter((row: any) => row.id !== id));
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows?.find((row: any) => row.id === id);
    if (editedRow?.isNew) {
      setRows(rows?.filter((row: any) => row.id !== id)); // حذف ردیف جدید در صورت انصراف
    }
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow = { ...newRow, isNew: false };

    // بروزرسانی تاریخ انقضا (expireDate) در ردیف‌ها
    setRows((oldRows: any) =>
      oldRows?.map((row: any) => (row.id === newRow.id ? updatedRow : row))
    );

    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const columns: GridColDef[] = [
    {
      field: "actions",
      type: "actions",
      headerName: "عملیات",
      width: 100,
      cellClassName: "actions",
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              sx={{
                color: "primary.main",
              }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="انصراف"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="ویرایش"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="حذف"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ];
      },
    },
    {
      field: "bankName",
      headerName: "بانک",
      minWidth: 150,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      sortable: true,
    },
    // {
    //   field: "accountNumber",
    //   headerName: "شماره حساب",
    //   minWidth: 180,
    //   filterable: true,
    //   align: "center",
    //   headerAlign: "center",
    //   flex: 1,
    //   editable: true,
    //   sortable: true,
    // },
    // {
    //   field: "shabaNumber",
    //   headerName: "شماره شبا",
    //   minWidth: 180,
    //   filterable: true,
    //   align: "center",
    //   headerAlign: "center",
    //   flex: 1,
    //   editable: true,
    //   sortable: true,
    // },
    {
      field: "cardNumber",
      headerName: "شماره کارت",
      minWidth: 180,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      sortable: true,
    },
    {
      field: "cvV2",
      headerName: "CVV2",
      minWidth: 150,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      sortable: true,
    },
    {
      field: "expireDate",
      headerName: "تاریخ انقضا",
      minWidth: 150,
      filterable: true,
      align: "center",
      headerAlign: "center",

      flex: 1,
      editable: true,
      sortable: true,
      //type: "date",
    },
    {
      field: "ownerName",
      headerName: "نام صاحب حساب",
      minWidth: 200,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      sortable: true,
    },
    {
      field: "nationalCode",
      headerName: "کد ملی",
      minWidth: 150,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      sortable: true,
    },
    {
      field: "mobile",
      headerName: "موبایل",
      minWidth: 150,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      sortable: true,
    },
    {
      field: "desc",
      headerName: "توضیحات",
      minWidth: 250,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      sortable: true,
    },
  ];

  React.useEffect(() => {
    if (
      userData &&
      userData.bankAccountsUser &&
      userData.bankAccountsUser.length > 0
    ) {
      setRows(userData.bankAccountsUser ?? []);
    }
  }, [userData]);

  return (
    <Box
      sx={{
        height: "auto",
        width: "100%",
        "& .actions": {
          color: "text.secondary",
        },
        "& .textPrimary": {
          color: "text.primary",
        },
      }}
    >
      <DataGrid
        rows={rows}
        key={"bankAcc"}
        columns={columns}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        slots={{
          toolbar: (props) => (
            <EditToolbarBankAccount
              onClose={onClose}
              userRef={userData?.id}
              existingBankAccount={userData?.bankAccountsUser}
              setUpdatingBankAccount={setRows}
              updatingBankAccount={rows}
              rows={props.rows}
              setRowModesModel={props.setRowModesModel}
              setRows={props.setRows}
              refetchUser={refetchUser}
            />
          ),
        }}
        slotProps={{
          toolbar: { setRows, setRowModesModel, rows },
        }}
      />
    </Box>
  );
}
